import React, { useEffect, useRef, useState } from 'react'
import './digital-vault-search.css'
import styles from './search.module.css'
import { Container, Grid, useMediaQuery } from '@material-ui/core'
import { Grid as DataGrid, GridColumn, GridNoRecords } from '@progress/kendo-react-grid'
import { ButtonHLPure, CheckBoxHLPure, DropDownBtnHLPure, SearchBarHLPure, SimpleLoading, Traslates } from '../../pures'
import { Popup } from '@progress/kendo-react-popup';
import { filterBy } from '@progress/kendo-data-query'
import { Pager } from '@progress/kendo-react-data-tools'
import { useTranslation } from 'react-i18next'
import { DigitalVaultDocumentAction } from '../../actions'
import { ArrowBtnMobile } from '../../../assets/logos/icons'
import { NIL } from 'uuid'

const initialDataState = {
    skip: 0,
    take: 6,
};

const initialFilter = {
    logic: 'and',
    filters: [],
};

const ActionsCell = (props) => {
    const { t } = useTranslation()

    return <td
        style={props.style}
        className={props.className}
        colSpan={props.colSpan}
        role={'gridcell'}
        aria-colindex={props.ariaColumnIndex}
        aria-selected={props.isSelected}
    >
        <div className='digitalVaultSearchView-cellActions'>
            <button
                className='digitalVaultSearchView-cellActions-btn'
                onClick={() => props.onClick(props.dataItem)}
            >
                {t('digitalVaultSearchView.grid.title.seeDocument')}
            </button>
            <button
                className='digitalVaultSearchView-cellActions-arrowbtn'
                onClick={() => props.onClick(props.dataItem)}
            >
                <img
                    className='cellActions-icon-arrow'
                    alt={'cellActions-arrow'}
                    src={ArrowBtnMobile}
                />
            </button>
        </div>
    </td>
}

const CheckboxCell = (props) => {
    const { field, dataItem } = props;

    return <td><CheckBoxHLPure checkedState={dataItem[field]} /></td>
}

const DigitalVaultSearchView = (props) => {
    const {
        isLoading,
        companies,
        selectedCompanyId,
        selectedCategoryId,
        documents,
        loadDocuments,
        noDataMessage,
        selectedDocument,
        setSelectedDocument
    } = props;

    const { t } = useTranslation();
    const anchor = useRef();
    const [showSearch, setShowSearch] = useState(false);

    const [ddCompanyId, setDdCompanyId] = useState(selectedCompanyId)
    const [categories, setCategories] = useState([]);
    const [ddCategoryId, setDdCategoryId] = useState(selectedCategoryId)

    const onCompanyDropdownChange = (e) => setDdCompanyId(e.target.value)
    const onCategoryDropdownChange = (e) => setDdCategoryId(parseInt(e.target.value))

    useEffect(() => {
        if (ddCompanyId === 0) return;
        const company = companies.find(c => c.id === ddCompanyId)
        let categoryOptions = ddCompanyId === NIL ? [...company.categories] : [{ id: 0, name: t('digitalVaultSearchView.popup.title.allCategories') }, ...company.categories]
        categoryOptions = categoryOptions.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
        setCategories(categoryOptions)
        setDdCategoryId(ddCompanyId === NIL ? categoryOptions[0].id : 0)
    }, [ddCompanyId])

    useEffect(() => {
        setDdCompanyId(selectedCompanyId)
    }, [selectedCompanyId])

    const [filter, setFilter] = useState(initialFilter);
    const [page, setPage] = useState(initialDataState);
    const handlePageChange = (e) => {
        setPage({
            skip: e.skip,
            take: e.take,
        });
    };

    const showArrowBtn = useMediaQuery('(max-width: 960px)');
    const showMobile = useMediaQuery('(max-width: 560px)');

    const ActionCell = (props) => <ActionsCell {...props} onClick={setSelectedDocument} />

    if (isLoading) return <SimpleLoading />

    if (selectedDocument) return <DigitalVaultDocumentAction
        documentId={selectedDocument.documentId}
        stateToMaintain={{ selectedCompanyId, selectedCategoryId, selectedDocument }}
        goBack={() => setSelectedDocument(null)}
    />

    const data = filterBy(documents, filter)

    const noResultsStyles = {
        color: "#2C353C",
        fontSize: "16px",
        fontWeight: 600,
        textAlign: "center"
    }

    return (
        <Container disableGutters className='digitalVaultSearchView'>
            {noDataMessage ? (
                <p style={noResultsStyles}>
                    {noDataMessage}
                </p>
            ) : (
                <Grid container spacing={3}>
                    <div className='digitalVaultSearchView-search-container'>
                        <div className='digitalVaultSearchView-search'>
                            <SearchBarHLPure
                                placeholder={t('digitalVaultSearchView.grid.placeholder.advancedSearch')}
                                disabled={true}
                            />
                            <button className='digitalVaultSearchView-btn-open-popup' ref={anchor} onClick={() => {
                                setShowSearch(prev => !prev);
                                setDdCompanyId(selectedCompanyId);
                                setDdCategoryId(selectedCategoryId);
                            }}>
                                <span class='k-icon k-font-icon k-i-arrow-overflow-down'></span>
                            </button>
                            <Popup
                                className={`${styles['popup-container-class']} k-popup.popup-content k-animation-container`}
                                anchor={anchor.current}
                                show={showSearch}
                                popupClass={'popup-content'}
                                anchorAlign={{ horizontal: 'right', vertical: 'bottom', }}
                                popupAlign={{ horizontal: 'right', vertical: 'top', }}
                            >
                                <p className={styles['popup-content-title']}>{t('digitalVaultSearchView.popup.title.selectTheField')}</p>

                                <DropDownBtnHLPure
                                    labelDropDown={t('digitalVaultSearchView.popup.title.company')}
                                    optionsDropDown={companies}
                                    optionsValue={ddCompanyId}
                                    valueItem={'id'}
                                    nameOption={'companyName'}
                                    onDropDownChange={onCompanyDropdownChange}
                                />

                                <DropDownBtnHLPure
                                    labelDropDown={t('digitalVaultSearchView.grid.title.category')}
                                    optionsDropDown={categories}
                                    optionsValue={ddCategoryId}
                                    valueItem={'id'}
                                    nameOption={'name'}
                                    onDropDownChange={onCategoryDropdownChange}
                                />

                                <div className='flex-buttons'>
                                    <ButtonHLPure
                                        nameBtn={t('button.close')}
                                        onClickBtn={() => setShowSearch(false)}
                                    />
                                    <ButtonHLPure
                                        nameBtn={t('button.search')}
                                        primary
                                        onClickBtn={async () => {
                                            setShowSearch(false);
                                            await loadDocuments(ddCompanyId, ddCategoryId, undefined, ddCompanyId === NIL);
                                        }}
                                    />
                                </div>
                            </Popup>
                        </div>
                    </div>
                    <Grid item xs={12}>
                        <Traslates>
                            <DataGrid
                                className='digitalVaultSearchView-grid'
                                data={data.slice(page.skip, page.take + page.skip)}
                                filterable={true}
                                filter={filter}
                                onFilterChange={(e) => setFilter(e.filter)}
                            >
                                <GridColumn field='documentName' title={t('digitalVaultSearchView.grid.title.name')} locked={true} width={showMobile ? '175px' : '250px'} />
                                <GridColumn field='categoryName' title={t('digitalVaultSearchView.grid.title.category')} width='200px' />
                                <GridColumn field='subcategoryName' title={t('digitalVaultSearchView.grid.title.subcategory')} width='200px' />
                                <GridColumn field='valueEntities' title={t('digitalVaultSearchView.grid.title.entities')} width='200px' />
                                <GridColumn field='tags' title={t('digitalVaultSearchView.grid.title.labels')} width='200px' />
                                <GridColumn field='variablesNames' title={t('digitalVaultSearchView.grid.title.variables')} width='200px' />
                                <GridColumn field='typeDocumentName' title={t('digitalVaultSearchView.grid.title.type.document')} width='200px' />
                                <GridColumn field='mandatory' title={t('digitalVaultSearchView.grid.title.obligatory')} width='170px' filter='boolean' cell={CheckboxCell} />
                                <GridColumn field='documentAlias' title={t('digitalVaultSearchView.grid.title.alias')} width='200px' />
                                <GridColumn field='penalized' title={t('documentPanelBarBasicData.penalty')} width='170px' filter='boolean' cell={CheckboxCell} />
                                <GridColumn field='documentStartDate' title={t('digitalVaultSearchView.grid.title.celebration.date')} filter='date' format='{0:d/MMM/y}' width='200px' />
                                <GridColumn field='documentValidation' title={t('digitalVaultSearchView.grid.title.validity')} filter='date' format='{0:d/MMM/y}' width='200px' />
                                <GridColumn title={showArrowBtn ? '' : t('digitalVaultSearchView.grid.title.actions')} cell={ActionCell} filterable={false} locked={true} width={showArrowBtn ? '35px' : '150px'} />

                                <GridNoRecords>
                                    {t('configurationDomainPanel.notResults')}
                                </GridNoRecords>
                            </DataGrid>
                        </Traslates>

                        {/* pager separado para que funcione el scroll */}
                        <Pager
                            skip={page.skip}
                            take={page.take}
                            onPageChange={handlePageChange}
                            total={data.length}
                            info
                            buttonCount={5}
                            type='numeric'
                            previousNext
                        />
                    </Grid>
                </Grid>
            )}

        </Container>
    )
}

export default DigitalVaultSearchView